import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ptBR } from "@mui/x-date-pickers/locales";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import colors from "assets/theme/base/colors";
import { useMaterialUIController } from "context";

import { getRevenueExpensesExport } from "../receitasxdespesas/revenueExpensesApi";

const MAX_EXPORT_MONTHS = 12;

const normalizeInitialMonth = (dateValue, fallbackDate) => {
  const parsedDate = dateValue ? dayjs(dateValue) : fallbackDate;
  return parsedDate.isValid() ? parsedDate.startOf("month") : fallbackDate.startOf("month");
};

const getInclusiveMonthCount = (startMonth, endMonth) => endMonth.diff(startMonth, "month") + 1;

const buildExportFileName = (startMonth, endMonth) =>
  `extrato-receitas-despesas-${startMonth.format("YYYY-MM")}-a-${endMonth.format("YYYY-MM")}.xlsx`;

const buildWorksheetRows = (items) =>
  items.map((item) => ({
    Data: item.makeDateString,
    Tipo: item.type === 1 ? "Receita" : "Despesa",
    Nome: item.name,
    Categoria: item.category ?? "",
    Valor: item.type === 1 ? Number(item.value ?? 0) : -Number(item.value ?? 0),
  }));

function RevenueExpenseExportDialog({
  open,
  onClose,
  openSnackBar,
  search,
  category,
  initialStartDate,
  initialEndDate,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const currentMonth = useMemo(() => dayjs().startOf("month"), []);
  const [startMonth, setStartMonth] = useState(currentMonth);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [isExporting, setIsExporting] = useState(false);
  const inputIconColor = darkMode ? "rgba(226, 232, 240, 0.82)" : colors.text.main;
  const pickerTextColor = darkMode ? "#f8fafc" : colors.dark.main;

  const datePickerTextFieldSx = useMemo(
    () => ({
      "& .MuiInputLabel-root": {
        color: darkMode ? "rgba(226, 232, 240, 0.74)" : colors.text.main,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: darkMode ? colors.info.main : colors.info.main,
      },
      "& .MuiOutlinedInput-root": {
        color: pickerTextColor,
        "& fieldset": {
          borderColor: darkMode ? "rgba(148, 163, 184, 0.46)" : undefined,
        },
        "&:hover fieldset": {
          borderColor: darkMode ? "rgba(226, 232, 240, 0.58)" : undefined,
        },
        "&.Mui-focused fieldset": {
          borderColor: colors.info.main,
        },
      },
      "& .MuiIconButton-root, & .MuiSvgIcon-root": {
        color: inputIconColor,
      },
    }),
    [darkMode, inputIconColor, pickerTextColor]
  );

  const pickerPaperSx = useMemo(
    () => ({
      backgroundColor: darkMode ? "#111827" : "#ffffff",
      backgroundImage: "none",
      border: darkMode ? "1px solid rgba(148, 163, 184, 0.18)" : undefined,
      boxShadow: darkMode ? "0 20px 60px rgba(2, 6, 23, 0.55)" : undefined,
      color: pickerTextColor,
    }),
    [darkMode, pickerTextColor]
  );

  const pickerLayoutSx = useMemo(
    () => ({
      color: pickerTextColor,
      "& .MuiPickersCalendarHeader-label": {
        color: pickerTextColor,
      },
      "& .MuiPickersCalendarHeader-switchViewButton, & .MuiPickersCalendarHeader-switchViewIcon": {
        color: inputIconColor,
      },
      "& .MuiPickersMonth-monthButton, & .MuiPickersYear-yearButton": {
        color: darkMode ? "rgba(226, 232, 240, 0.86)" : colors.dark.main,
      },
      "& .MuiPickersMonth-monthButton:hover, & .MuiPickersYear-yearButton:hover": {
        backgroundColor: darkMode ? "rgba(59, 130, 246, 0.18)" : "rgba(26, 115, 232, 0.08)",
      },
      "& .MuiPickersMonth-monthButton.MuiPickersMonth-selected, & .MuiPickersYear-yearButton.MuiPickersYear-selected": {
        backgroundColor: colors.info.main,
        color: "#ffffff",
      },
      "& .MuiPickersMonth-monthButton.MuiPickersMonth-selected:hover, & .MuiPickersYear-yearButton.MuiPickersYear-selected:hover": {
        backgroundColor: colors.info.focus,
      },
    }),
    [darkMode, inputIconColor, pickerTextColor]
  );

  const selectedPickerButtonSx = useMemo(
    () => ({
      "&.MuiPickersMonth-selected, &.MuiPickersYear-selected": {
        backgroundColor: `${colors.info.main} !important`,
        color: "#ffffff !important",
      },
      "&.MuiPickersMonth-selected:hover, &.MuiPickersMonth-selected:focus, &.MuiPickersYear-selected:hover, &.MuiPickersYear-selected:focus": {
        backgroundColor: `${colors.info.focus} !important`,
      },
    }),
    []
  );

  const datePickerSlotProps = useMemo(
    () => ({
      textField: {
        fullWidth: true,
        sx: datePickerTextFieldSx,
      },
      openPickerButton: {
        sx: {
          color: inputIconColor,
        },
      },
      openPickerIcon: {
        sx: {
          color: inputIconColor,
        },
      },
      desktopPaper: {
        sx: pickerPaperSx,
      },
      mobilePaper: {
        sx: pickerPaperSx,
      },
      layout: {
        sx: pickerLayoutSx,
      },
      monthButton: {
        sx: selectedPickerButtonSx,
      },
      yearButton: {
        sx: selectedPickerButtonSx,
      },
    }),
    [datePickerTextFieldSx, inputIconColor, pickerLayoutSx, pickerPaperSx, selectedPickerButtonSx]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextStartMonth = normalizeInitialMonth(initialStartDate, currentMonth);
    const nextEndMonth = normalizeInitialMonth(initialEndDate, nextStartMonth);

    setStartMonth(nextStartMonth);
    setEndMonth(nextEndMonth);
  }, [currentMonth, initialEndDate, initialStartDate, open]);

  const handleExport = async () => {
    if (!startMonth || !endMonth) {
      openSnackBar?.({ type: "error", message: "Selecione o período que deve ser exportado." });
      return;
    }

    const normalizedStartMonth = startMonth.startOf("month");
    const normalizedEndMonth = endMonth.startOf("month");
    const inclusiveMonthCount = getInclusiveMonthCount(normalizedStartMonth, normalizedEndMonth);

    if (normalizedEndMonth.isBefore(normalizedStartMonth)) {
      openSnackBar?.({ type: "error", message: "O mês final deve ser igual ou posterior ao mês inicial." });
      return;
    }

    if (inclusiveMonthCount > MAX_EXPORT_MONTHS) {
      openSnackBar?.({ type: "error", message: "Selecione no máximo 12 meses para exportar." });
      return;
    }

    setIsExporting(true);

    try {
      const items = await getRevenueExpensesExport(
        search,
        category,
        normalizedStartMonth.format("YYYY-MM-DD"),
        normalizedEndMonth.endOf("month").format("YYYY-MM-DD")
      );

      if (!items?.length) {
        openSnackBar?.({ type: "alert", message: "Nenhum lançamento encontrado no período selecionado." });
        return;
      }

      const XLSX = await import("xlsx");
      const worksheet = XLSX.utils.json_to_sheet(buildWorksheetRows(items));
      worksheet["!cols"] = [
        { wch: 14 },
        { wch: 14 },
        { wch: 28 },
        { wch: 24 },
        { wch: 14 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Extrato");
      XLSX.writeFile(workbook, buildExportFileName(normalizedStartMonth, normalizedEndMonth));

      openSnackBar?.({ type: "success", message: "Extrato exportado com sucesso." });
      onClose?.();
    } catch (error) {
      openSnackBar?.({ type: "error", message: `${error.message ?? error}` });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isExporting ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? "#0f172a" : "#ffffff",
          color: darkMode ? "#f8fafc" : undefined,
          backgroundImage: "none",
          border: darkMode ? "1px solid rgba(148, 163, 184, 0.2)" : undefined,
          boxShadow: darkMode ? "0 24px 80px rgba(2, 6, 23, 0.5)" : undefined,
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: darkMode ? "rgba(2, 6, 23, 0.72)" : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: darkMode ? "#f8fafc" : undefined,
          borderBottom: darkMode ? "1px solid rgba(148, 163, 184, 0.16)" : undefined,
          pb: 2,
        }}
      >
        Exportar extrato
      </DialogTitle>
      <DialogContent sx={{ pt: "20px !important" }}>
        <LocalizationProvider
          adapterLocale="pt-br"
          dateAdapter={AdapterDayjs}
          localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
        >
          <MDBox display="grid" gap={2}>
            <DatePicker
              label="Mês inicial"
              views={["year", "month"]}
              openTo="month"
              format="MM/YYYY"
              value={startMonth}
              onChange={setStartMonth}
              slotProps={datePickerSlotProps}
            />
            <DatePicker
              label="Mês final"
              views={["year", "month"]}
              openTo="month"
              format="MM/YYYY"
              value={endMonth}
              onChange={setEndMonth}
              minDate={startMonth ?? undefined}
              slotProps={datePickerSlotProps}
            />
          </MDBox>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: darkMode ? "1px solid rgba(148, 163, 184, 0.16)" : undefined,
        }}
      >
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={isExporting}>
          Cancelar
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={handleExport} disabled={isExporting}>
          {isExporting ? <CircularProgress color="inherit" size={16} /> : "Exportar"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

RevenueExpenseExportDialog.defaultProps = {
  category: "",
  initialEndDate: null,
  initialStartDate: null,
  onClose: undefined,
  openSnackBar: undefined,
  search: "",
};

RevenueExpenseExportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  openSnackBar: PropTypes.func,
  search: PropTypes.string,
  category: PropTypes.string,
  initialStartDate: PropTypes.string,
  initialEndDate: PropTypes.string,
};

export default RevenueExpenseExportDialog;
